import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, CartesianGrid,
} from "recharts";
import { Download, SlidersHorizontal, X } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const STAGE_COLORS = [
  "#94A3B8", "#CBD5E1", "#64748B", "#6366F1", "#F59E0B",
  "#F43F5E", "#8B5CF6", "#F97316", "#EA580C", "#22C55E",
];

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const cols = Array.from(
    rows.reduce((acc, r) => {
      Object.keys(r).forEach((k) => acc.add(k));
      return acc;
    }, new Set())
  );
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") return `"${JSON.stringify(v).replace(/"/g, '""')}"`;
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}-${date}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function fmtMonth(s) {
  if (!s) return s;
  try {
    const [y, m] = s.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("default", { month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

export default function Reports() {
  const [funnel, setFunnel] = useState([]);
  const [sources, setSources] = useState([]);
  const [board, setBoard] = useState([]);
  const [lost, setLost] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState({ months: [], agents: [], stages: [], cells: [] });
  const [movLoading, setMovLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState("__all__");
  const [tis, setTis] = useState({ agents: [], stages: [], cells: [] });
  const [tisLoading, setTisLoading] = useState(true);
  const [options, setOptions] = useState({
    lead_owners: [], categories: [], opportunity_types: [],
    lead_sources: [], created_months: [], last_activity_months: [],
  });
  const [filters, setFilters] = useState({
    lead_owner_ids: [], categories: [], opportunity_types: [],
    lead_sources: [], created_months: [], last_activity_months: [],
  });

  // Build query string from current filters (URLSearchParams handles array repeats)
  const queryString = useMemo(() => {
    const sp = new URLSearchParams();
    Object.entries(filters).forEach(([k, vals]) => {
      (vals || []).forEach((v) => sp.append(k, v));
    });
    return sp.toString();
  }, [filters]);

  useEffect(() => {
    // Filter options are static-ish; fetch once
    (async () => {
      try {
        const { data } = await api.get("/reports/filter-options");
        setOptions(data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/reports/all${queryString ? `?${queryString}` : ""}`);
        setFunnel(data.funnel || []);
        setSources(data.sources || []);
        setBoard(data.leaderboard || []);
        setLost(data.lost_reasons || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [queryString]);

  // Stage-movement monthly data (independent from the /reports/all filters —
  // uses its own agent selection via the dropdown below the chart).
  useEffect(() => {
    setMovLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/reports/stage-movements-monthly");
        setMovements(data || { months: [], agents: [], stages: [], cells: [] });
      } finally {
        setMovLoading(false);
      }
    })();
  }, []);

  // Time-in-Stage heatmap data
  useEffect(() => {
    setTisLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/reports/time-in-stage");
        setTis(data || { agents: [], stages: [], cells: [] });
      } finally {
        setTisLoading(false);
      }
    })();
  }, []);

  // Build lookup: `${agent_id}__${stage}` → cell for O(1) table rendering
  const tisMap = useMemo(() => {
    const m = new Map();
    (tis.cells || []).forEach((c) => m.set(`${c.agent_id}__${c.stage}`, c));
    return m;
  }, [tis]);

  // Compute min/max across all avg_days for heatmap colour scaling
  const tisRange = useMemo(() => {
    const vals = (tis.cells || []).map((c) => c.avg_days).filter((x) => Number.isFinite(x));
    if (vals.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [tis]);

  // Heatmap colour: low = emerald (fast/good), high = rose (slow/bad).
  // Linear interpolation clamped between the min/max of the current dataset.
  const heatColor = (val) => {
    if (val === undefined || val === null || !Number.isFinite(val)) return "";
    const { min, max } = tisRange;
    if (max === min) return "bg-emerald-50 text-emerald-700";
    const t = (val - min) / (max - min);
    if (t < 0.2) return "bg-emerald-50 text-emerald-700";
    if (t < 0.4) return "bg-lime-50 text-lime-700";
    if (t < 0.6) return "bg-amber-50 text-amber-700";
    if (t < 0.8) return "bg-orange-50 text-orange-700";
    return "bg-rose-50 text-rose-700 font-semibold";
  };

  const movementChartData = useMemo(() => {
    // Pivot cells into per-month rows with one column per stage. Filter by
    // selected agent (or aggregate across all agents when __all__).
    const cells = movements.cells || [];
    const filtered = selectedAgent === "__all__"
      ? cells
      : cells.filter((c) => c.agent_id === selectedAgent);
    const byMonth = new Map();
    for (const c of filtered) {
      const row = byMonth.get(c.month) || { month: c.month };
      row[c.stage] = (row[c.stage] || 0) + c.count;
      byMonth.set(c.month, row);
    }
    // Ensure ordered rows and zero-fill missing stages so the tooltip is complete
    return (movements.months || []).map((m) => {
      const row = byMonth.get(m) || { month: m };
      (movements.stages || []).forEach((s) => { if (row[s] === undefined) row[s] = 0; });
      return { ...row, monthLabel: fmtMonth(m) };
    });
  }, [movements, selectedAgent]);

  const movementTotal = useMemo(
    () => movementChartData.reduce((acc, r) => acc + (movements.stages || []).reduce((a, s) => a + (r[s] || 0), 0), 0),
    [movementChartData, movements.stages]
  );

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      lead_owner_ids: [], categories: [], opportunity_types: [],
      lead_sources: [], created_months: [], last_activity_months: [],
    });
  };

  const activeCount = Object.values(filters).reduce((acc, v) => acc + (v?.length || 0), 0);

  return (
    <Layout title="Reports & Analytics" breadcrumb={["Reports"]}>
      <FilterBar
        options={options}
        filters={filters}
        toggle={toggleFilter}
        clearAll={clearAllFilters}
        activeCount={activeCount}
      />

      <div className="mb-5 flex justify-end items-center gap-2">
        {loading && <span className="text-xs text-slate-500">Loading…</span>}
        <button
          data-testid="reports-download-all-btn"
          onClick={() => {
            const all = [
              ...funnel.map((r) => ({ report: "Funnel", ...r })),
              ...sources.map((r) => ({ report: "Source Performance", ...r })),
              ...board.map((r) => ({ report: "Leaderboard", ...r })),
              ...lost.map((r) => ({ report: "Lost Reasons", ...r })),
            ];
            downloadCsv("lms-all-reports", all);
          }}
          className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1.5"
        >
          <Download size={14} /> Download all (CSV)
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Pipeline funnel" onDownload={() => downloadCsv("pipeline-funnel", funnel)} downloadTestId="download-funnel">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel}>
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {funnel.map((row, i) => <Cell key={row.stage || `f-${i}`} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Lost reason mix" onDownload={() => downloadCsv("lost-reasons", lost)} downloadTestId="download-lost-reasons">
          <div className="h-72 grid place-items-center">
            {lost.length === 0 ? (
              <div className="text-sm text-slate-500">No lost leads in scope.</div>
            ) : (
              <div className="w-full h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={lost} dataKey="count" nameKey="reason" outerRadius={90} label>
                      {lost.map((row, i) => <Cell key={row.reason || `l-${i}`} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        <Card title="Source performance" onDownload={() => downloadCsv("source-performance", sources)} downloadTestId="download-sources">
          <table className="w-full text-sm">
            <thead><tr>{["Source", "Total", "Won", "Conv %"].map((h) => <th key={h} className="text-left py-2 text-xs uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
            <tbody>
              {sources.map((r) => (
                <tr key={r.lead_source} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{r.lead_source}</td>
                  <td className="py-2">{r.total}</td>
                  <td className="py-2 text-emerald-700">{r.live}</td>
                  <td className="py-2"><span className="text-orange-600 font-semibold">{r.conversion_rate}%</span></td>
                </tr>
              ))}
              {sources.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No data</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card title={`Pipeline leaderboard`} onDownload={() => downloadCsv("leaderboard", board)} downloadTestId="download-leaderboard" className="lg:col-span-2">
          <table className="w-full text-sm">
            <thead><tr>{["Owner", "Total", "Won", "Lost", "Conv %"].map((h) => <th key={h} className="text-left py-2 text-xs uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
            <tbody>
              {board.map((r) => (
                <tr key={r.owner} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{r.owner}</td>
                  <td className="py-2">{r.total}</td>
                  <td className="py-2 text-emerald-700">{r.live}</td>
                  <td className="py-2 text-rose-600">{r.lost}</td>
                  <td className="py-2 text-orange-600 font-semibold">{r.conversion_rate}%</td>
                </tr>
              ))}
              {board.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No data</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card
          title="Stage movement — monthly"
          onDownload={() => downloadCsv("stage-movements-monthly", (movements.cells || []).filter(c => selectedAgent === "__all__" || c.agent_id === selectedAgent))}
          downloadTestId="download-stage-movements"
          className="lg:col-span-2"
        >
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600">Agent:</label>
              <select
                data-testid="stage-movement-agent-select"
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white min-w-[200px]"
              >
                <option value="__all__">All agents (sum)</option>
                {(movements.agents || []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="text-xs text-slate-500">
              {movLoading ? "Loading…" : `${movementTotal} stage movement${movementTotal === 1 ? "" : "s"} across ${(movementChartData.length || 0)} month${movementChartData.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {movementChartData.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No stage movements recorded yet.
            </div>
          ) : (
            <>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={movementChartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(movements.stages || []).map((s, i) => (
                      <Bar
                        key={s}
                        dataKey={s}
                        stackId="a"
                        fill={STAGE_COLORS[i % STAGE_COLORS.length]}
                        radius={i === (movements.stages || []).length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-xs text-slate-500">
                Each bar = number of leads that <em>entered</em> a stage during that month.
                {selectedAgent === "__all__"
                  ? " Currently showing the sum across ALL agents."
                  : ` Filtered to agent's transitions only.`}
              </div>
            </>
          )}
        </Card>

        <Card
          title="Time in stage — by agent (avg days)"
          onDownload={() => downloadCsv("time-in-stage", tis.cells || [])}
          downloadTestId="download-time-in-stage"
          className="lg:col-span-2"
        >
          <div className="text-xs text-slate-500 mb-3">
            Avg days a lead spent in each stage before moving on. Attribution: the agent who transitioned the lead INTO that stage owns the duration. Only closed transitions counted (leads still sitting in their current stage are excluded).
          </div>

          {tisLoading ? (
            <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
          ) : (tis.cells || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">
              No closed stage transitions to analyse yet.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm" data-testid="time-in-stage-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500 font-medium">
                      Agent
                    </th>
                    {(tis.stages || []).map((s) => (
                      <th key={s} className="text-center py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(tis.agents || []).map((a) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="sticky left-0 bg-white py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{a.name}</td>
                      {(tis.stages || []).map((s) => {
                        const cell = tisMap.get(`${a.id}__${s}`);
                        if (!cell) {
                          return (
                            <td key={s} className="text-center py-2 px-3 text-slate-300">—</td>
                          );
                        }
                        return (
                          <td
                            key={s}
                            data-testid={`tis-cell-${a.id}-${s}`}
                            className={`text-center py-2 px-3 rounded transition-colors ${heatColor(cell.avg_days)}`}
                            title={`Sample: ${cell.sample_size} · min ${cell.min_days}d · max ${cell.max_days}d`}
                          >
                            <div className="font-semibold tabular-nums">{cell.avg_days}d</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">n={cell.sample_size}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex items-center gap-3 text-[11px] text-slate-500">
                <span className="font-medium">Colour scale:</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span>fast</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span>medium</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-300"></span>slow</span>
                <span className="ml-2">· Hover any cell to see sample size and range.</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

function FilterBar({ options, filters, toggle, clearAll, activeCount }) {
  const [openKey, setOpenKey] = useState(null);
  const groups = [
    { key: "lead_owner_ids",       label: "Lead Owner",           items: (options.lead_owners || []).map((o) => ({ value: o.id, label: o.name })) },
    { key: "categories",           label: "Category",             items: (options.categories || []).map((c) => ({ value: c, label: c })) },
    { key: "opportunity_types",    label: "Interested Services",  items: (options.opportunity_types || []).map((o) => ({ value: o, label: o })) },
    { key: "lead_sources",         label: "Lead Source",          items: (options.lead_sources || []).map((s) => ({ value: s, label: s })) },
    { key: "created_months",       label: "Lead Creation Month",  items: (options.created_months || []).map((m) => ({ value: m, label: fmtMonth(m) })) },
    { key: "last_activity_months", label: "Last Activity Month",  items: (options.last_activity_months || []).map((m) => ({ value: m, label: fmtMonth(m) })) },
  ];

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-xl p-3 shadow-sm" data-testid="reports-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5 mr-1">
          <SlidersHorizontal size={12} /> Filters
        </div>
        {groups.map((g) => {
          const selected = filters[g.key] || [];
          const isOpen = openKey === g.key;
          return (
            <div key={g.key} className="relative">
              <button
                data-testid={`filter-trigger-${g.key}`}
                onClick={() => setOpenKey(isOpen ? null : g.key)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${selected.length > 0 ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
              >
                {g.label}
                {selected.length > 0 && <span className="bg-white/30 rounded-full px-1.5 py-px text-[10px] font-bold">{selected.length}</span>}
              </button>
              {isOpen && (
                <FilterPopover
                  groupKey={g.key}
                  items={g.items}
                  selected={selected}
                  onToggle={(v) => toggle(g.key, v)}
                  onClose={() => setOpenKey(null)}
                  onClear={() => filters[g.key]?.forEach((v) => toggle(g.key, v))}
                />
              )}
            </div>
          );
        })}
        {activeCount > 0 && (
          <button
            data-testid="filter-clear-all"
            onClick={clearAll}
            className="ml-1 text-xs font-medium px-2 py-1 text-rose-600 hover:bg-rose-50 rounded-md inline-flex items-center gap-1"
          >
            <X size={11} /> Clear all ({activeCount})
          </button>
        )}
      </div>
    </div>
  );
}

function FilterPopover({ groupKey, items, selected, onToggle, onClose, onClear }) {
  const [search, setSearch] = useState("");
  const filtered = items.filter((it) => it.label?.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute z-40 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2" data-testid={`filter-popover-${groupKey}`}>
        <input
          autoFocus
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded mb-1.5"
        />
        <div className="max-h-60 overflow-y-auto">
          {filtered.length === 0 && <div className="text-xs text-slate-400 px-2 py-2">No options</div>}
          {filtered.map((it) => (
            <label key={it.value} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(it.value)}
                onChange={() => onToggle(it.value)}
                className="accent-orange-500"
              />
              <span className="text-xs text-slate-700 truncate">{it.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-slate-100 mt-1.5 pt-1.5 flex justify-end">
            <button onClick={onClear} className="text-[11px] text-rose-600 hover:underline">Clear</button>
          </div>
        )}
      </div>
    </>
  );
}

function Card({ title, children, className = "", onDownload, downloadTestId }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {onDownload && (
          <button
            data-testid={downloadTestId}
            onClick={onDownload}
            className="text-xs font-medium text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-orange-50"
            title="Download as CSV"
          >
            <Download size={12} /> CSV
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
